import { parse } from "@conform-to/zod";
import { cssBundleHref } from "@remix-run/css-bundle";
import {
  json,
  type DataFunctionArgs,
  type HeadersFunction,
  type LinksFunction,
  type MetaFunction } from
"@remix-run/node";
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useFetchers,
  useLoaderData,
  useMatches } from
"@remix-run/react";
import { withSentry } from "@sentry/remix";
import { AuthenticityTokenProvider } from "remix-utils/csrf/react";
import { HoneypotProvider } from "remix-utils/honeypot/react";
import { GeneralErrorBoundary } from "./components/error-boundary";
import Footer from "./components/footer";
import { EpicProgress } from "./components/progress-bar";
import { EpicToaster } from "./components/toaster";
import { href as iconsHref } from "./components/ui/icon";
import { ThemeFormSchema } from "./routes/switch-theme";
import tailwindStyleSheetUrl from "./styles/tailwind.css";
import { getUserId, logout } from "./utils/auth.server";
import { ClientHintCheck, getHints } from "./utils/client-hints";
import { csrf } from "./utils/csrf.server";
import { prisma } from "./utils/db.server";
import { getEnv } from "./utils/env.server";
import { honeypot } from "./utils/honeypot.server";
import {
  subscribeToNewsletter,
  type MailerLiteError } from
"./utils/mailerlite.server";
import { combineHeaders, getDomainUrl } from "./utils/misc";
import { useNonce } from "./utils/nonce-provider";
import { getTheme, type Theme } from "./utils/theme.server";
import { makeTimings, time } from "./utils/timing.server";
import { createToastHeaders, getToast } from "./utils/toast.server";

export const links: LinksFunction = () => {
  return [
  // Preload svg sprite as a resource to avoid render blocking
  { rel: "preload", href: iconsHref, as: "image" },
  // Preload CSS as a resource to avoid render blocking
  { rel: "preload", href: tailwindStyleSheetUrl, as: "style" },
  cssBundleHref ? { rel: "preload", href: cssBundleHref, as: "style" } : null,
  { rel: "mask-icon", href: "/favicons/mask-icon.svg" },
  {
    rel: "alternate icon",
    type: "image/png",
    href: "/favicons/favicon-32x32.png"
  },
  { rel: "apple-touch-icon", href: "/favicons/apple-touch-icon.png" }, (
  {
    rel: "manifest",
    href: "/site.webmanifest",
    crossOrigin: "use-credentials"
  } as const), // necessary to make typescript happy
  //These should match the css preloads above to avoid css as render blocking resource
  { rel: "icon", type: "image/svg+xml", href: "/favicons/favicon.svg" },
  { rel: "stylesheet", href: tailwindStyleSheetUrl },
  cssBundleHref ? { rel: "stylesheet", href: cssBundleHref } : null].
  filter(Boolean);
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [{ title: data ? "Present Parent" : "Error | Present Parent" }];
};

export async function action({ request }: DataFunctionArgs) {
  const formData = await request.formData();
  const email = formData.get("email-address");

  if (typeof email !== "string" || email.length === 0) {
    return json({ error: "Email is required" }, { status: 400 });
  }

  try {
    await subscribeToNewsletter(email);
    return json(
      { success: true },
      {
        headers: await createToastHeaders({
          title: "Success",
          description: "You've been subscribed to the newsletter.",
          type: "success"
        })
      }
    );
  } catch (error) {
    return json(
      { error: (error as MailerLiteError).message || "An error occurred" },
      { status: 500 }
    );
  }
}

export async function loader({ request }: DataFunctionArgs) {
  const timings = makeTimings("root loader");
  const userId = await time(() => getUserId(request), {
    timings,
    type: "getUserId",
    desc: "getUserId in root"
  });

  const user = userId ?
  await time(
    () =>
    prisma.user.findUniqueOrThrow({
      select: {
        id: true,
        name: true,
        username: true,
        tenantId: true,
        stripeCustomerId: true,
        stripeSubscriptionId: true,
        stripePriceId: true,
        stripeSubscriptionEndOrRenew: true,
        image: { select: { id: true } },
        roles: {
          select: {
            name: true,
            permissions: {
              select: { entity: true, action: true, access: true }
            }
          }
        }
      },
      where: { id: userId }
    }),
    { timings, type: "find user", desc: "find user in root" }
  ) :
  null;

  if (userId && !user) {
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await logout({ request, redirectTo: "/" });
  }

  const { toast, headers: toastHeaders } = await getToast(request);
  const honeyProps = honeypot.getInputProps();
  const [csrfToken, csrfCookieHeader] = await csrf.commitToken();

  return json(
    {
      user,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: {
          theme: getTheme(request)
        }
      },
      ENV: getEnv(),
      toast,
      honeyProps,
      csrfToken
    },
    {
      headers: combineHeaders(
        { "Server-Timing": timings.toString() },
        toastHeaders,
        csrfCookieHeader ? { "set-cookie": csrfCookieHeader } : null
      )
    }
  );
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    "Server-Timing": loaderHeaders.get("Server-Timing") ?? ""
  };
  return headers;
};

function Document({
  children,
  nonce,
  theme = "light",
  env = {}





}: {children: React.ReactNode;nonce: string;theme?: Theme;env?: Record<string, string>;}) {
  return (
    <html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-765C3HS2CR">
        </script>
				<script
          dangerouslySetInnerHTML={{
            __html: `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-765C3HS2CR');
    `
          }}>
        </script>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<meta name="og:title" content="Present Parent" />
				<meta
          name="og:description"
          content="Present Parent AI is a platform that helps you plan checklists for the future of your family – along with activities and other resources" />

				<meta
          name="og:image"
          content="https://presentparent.ai/img/og-image.png" />

				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:site" content="@PresentParentAI" />
				<meta name="twitter:creator" content="@PresentParentAI" />
				<meta name="twitter:title" content="Present Parent" />
				<meta
          name="twitter:description"
          content="Present Parent AI is a platform that helps you plan checklists for the future of your family – along with activities and other resources" />

				<meta
          name="twitter:image"
          content="https://present-parent.fly.dev/img/og-image.png" />

				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`
          }} />

				<script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');`
          }}>
        </script>
				<script
          async
          src="https://r.wdfl.co/rw.js"
          data-rewardful="74ad0b"
          nonce={nonce}>
        </script>

				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
				<LiveReload nonce={nonce} />
			</body>
		</html>);

}

function App() {
  const data = useLoaderData<typeof loader>();
  const nonce = useNonce();
  // const user = useOptionalUser()
  const theme = useTheme();

  return (
    <Document nonce={nonce} theme={theme} env={data.ENV}>
			<div className="flex h-screen flex-col justify-between">
				<div className="flex-1">
					<div className="p-2 md:container">
						<Outlet />
					</div>
				</div>
				<Footer />
			</div>
			<EpicToaster toast={data.toast} />
			<EpicProgress />
		</Document>);

}

function AppWithProviders() {
  const data = useLoaderData<typeof loader>();
  return (
    <AuthenticityTokenProvider token={data.csrfToken}>
			<HoneypotProvider {...data.honeyProps}>
				<App />
			</HoneypotProvider>
		</AuthenticityTokenProvider>);

}

export default withSentry(AppWithProviders);

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme(): Theme | undefined {
  const matches = useMatches();
  const rootData = (matches.find((match) => match.id === "root")?.data as {
    requestInfo: {userPrefs: {theme: Theme;};};
  });
  const hints = getHints();
  const theme = rootData?.requestInfo?.userPrefs?.theme;
  return theme ?? hints.theme;
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
  const fetchers = useFetchers();
  const themeFetcher = fetchers.find((f) => f.formAction === "/switch-theme");

  if (themeFetcher && themeFetcher.formData) {
    const submission = parse(themeFetcher.formData, {
      schema: ThemeFormSchema
    });
    return submission.value?.theme;
  }
}

export function ErrorBoundary() {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce();

  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    <Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>);

}